import { Component, OnInit, Input, Output, ContentChildren, TemplateRef, QueryList, Directive, ContentChild, EventEmitter } from '@angular/core';

import { ColDef, RowSelectionOptions, GridReadyEvent, GridApi, IRowNode, SelectionChangedEvent, AllCommunityModule } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { AgGridRowOptionsCellRenderer } from './ag-grid-row-options.component';
import { AgGridCustomCellRenderer } from './ag-grid-cell-renderer.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Directive({
  standalone: false,
  selector: '[agGridCellDisplayControlDef]'
})
export class AgGridCellDisplayControlDef {
  constructor(public template: TemplateRef<any>) { }
}

@Directive({
  standalone: false,
  selector: '[agGridColumnDef]'
})
export class AgGridColumnDef {
  @Input('agGridColumnDef')
  get id(): string {
    return this._id;
  }
  set id(value: string) {
    this._id = value;
  }
  protected _id: string;

  @Input() field: string;

  @Input() filterText: (data) => string;

  @Input() tooltipText: (data) => string;

  /** Relative width of the column; columns default to 1 (equal widths). */
  @Input() flex: number;

  @ContentChild(AgGridCellDisplayControlDef) displayControlDef: AgGridCellDisplayControlDef;

  constructor() {
  }
}


@Component({
  standalone: false,
  selector: 'datex-ag-grid',
  templateUrl: './ag-grid.component.html'
})
export class AgGridComponent implements OnInit {

  private gridApi!: GridApi;

  @Input() showFilter: boolean = false;
  @Input() totalCount: number = 0;
  @Input() selection: boolean = false;
  @Input() selectionModeSingle: boolean = false;
  @Input() rows: any[];
  @Input() rowCommandTemplateRef: TemplateRef<any>;
  @Input() showSelectedOnlyToggle = false;
  @Input() isRowSelectable = (node: any) => true;
  @Input() rowClassRules: { [key: string]: ((params: any) => boolean) | string } = undefined;
  @Input() getRowId: (params: any) => string = undefined;
  /** Row height in px; null uses the ag-grid theme default. Set to fit multi-line cells. */
  @Input() rowHeight: number = null;
  /** Hides the spreadsheet-style cell focus border — set for list-style grids
   *  where cells contain links/controls rather than editable data. */
  @Input() suppressCellFocus = false;

  @Output() selectionChanged: EventEmitter<void> = new EventEmitter();

  showOnlySelected = false;


  @ContentChildren(AgGridColumnDef, { descendants: true }) _contentColumnDefs: QueryList<AgGridColumnDef>;
  _columnDefsByName = new Map<string, AgGridColumnDef>();


  defaultColDef = {
    filter: true,
    flex: 1,
  };

  private _fullTextSearch: string = '';
  @Input()
  set fullTextSearch(value: string) {
    this._fullTextSearch = value;
    this.onFilterInputChanged();
  }
  get fullTextSearch(): string {
    return this._fullTextSearch;
  }


  agColDefs: ColDef[] = [];
  agSelection: RowSelectionOptions;


  get selected() {
    return this.gridApi?.getSelectedRows() || [];
  }

  get selectedCount() {
    return this.selected.length;
  }

  get hasSelected() {
    return this.selectedCount > 0;
  }

  constructor() {
  }

  onAgGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    this.selectionChanged.emit()
  }

  ngOnInit(): void {
  }

  ngAfterContentInit() {
    this._cacheColumnDefs();
    this._initializeAgGridOptions();
  }

  ngOnDestroy() {
    this._columnDefsByName.clear();
  }

  deselectAll() {
    this.gridApi.deselectAll();
  }

  select(ids: number[]) {
    for (const id of ids) {
      const rowNode = this.gridApi.getRowNode(id.toString());
      if (rowNode) {
        rowNode.setSelected(true);
      }
    }
  }

  redrawRows() {
    this.gridApi.redrawRows();
  }

  private _cacheColumnDefs() {
    this._columnDefsByName.clear();

    this._contentColumnDefs.forEach(columnDef => {
      if (this._columnDefsByName.has(columnDef.id)) {
        throw new Error(`Duplicate column definition for ${columnDef.id}`);
      }
      this._columnDefsByName.set(columnDef.id, columnDef);
    });
  }

  private _initializeAgGridOptions() {
    if (this.selection) {
      this.agSelection = {
        mode: this.selectionModeSingle ? "singleRow" : "multiRow",
        headerCheckbox: true,
        enableClickSelection: false,
        selectAll: 'filtered',
        isRowSelectable: (node) => this.isRowSelectable(node.data)
      };
    }

    this._contentColumnDefs.forEach(columnDef => {
      const agGridColDef =
        {
          headerName: columnDef.id,
          colId: columnDef.id,
          field: columnDef.field,
          sortable: false,
          cellRenderer: AgGridCustomCellRenderer,
          cellRendererParams: {
            columnDef: columnDef
          }
        } as ColDef<any, any>;
      if (columnDef.flex) {
        agGridColDef.flex = columnDef.flex;
      }
      if (columnDef.filterText && columnDef.field) {
        //agGridColDef.getQuickFilterText = (params) => columnDef.filterText(params.value);
        agGridColDef.valueGetter = (params) => columnDef.filterText(params.data[columnDef.field]);
      }
      if (columnDef.tooltipText && columnDef.field) {
        agGridColDef.tooltipValueGetter = (params) => columnDef.tooltipText(params.data[columnDef.field]);
      }
      this.agColDefs.push(agGridColDef);
    });


    if (this.rowCommandTemplateRef) {
      const columnSize = 40;
      this.agColDefs.push(
        {
          maxWidth: columnSize,
          minWidth: columnSize,
          headerName: '',
          colId: '$rowOptions',
          pinned: 'right',
          resizable: false,
          cellRenderer: AgGridRowOptionsCellRenderer,
          cellRendererParams: {
            rowCommandTemplateRef: this.rowCommandTemplateRef
          },
          hide: false,
          sortable: false,
          suppressHeaderMenuButton: true,
        }
      );
    }
  }

  onShowOnlyToggleChange(event: MatSlideToggleChange) {
    this.showOnlySelected = event.checked;
    this.gridApi!.onFilterChanged();
  }

  isExternalFilterPresent(): boolean {
    return this?.showOnlySelected;
  }

  doesExternalFilterPass(node: IRowNode<any>) {
    return node?.isSelected();
  }

  onFilterInputChanged() {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setGridOption(
      "quickFilterText",
      this.fullTextSearch,
    );
  }
}